import { useState } from 'react';
import { ArrowPathIcon, WrenchScrewdriverIcon, DocumentTextIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

// ── Types ─────────────────────────────────────────────────────────────────────

export type StatTone = 'ok' | 'warn' | 'mute';

export interface StatTile {
  label: string;
  value: number;
  tone?: StatTone;
}

export interface ColumnDef {
  key: string;
  label: string;
}

export interface RepairConfig<Scan, Repair> {
  label: (d: Scan) => string;
  enabled: (d: Scan) => boolean;
  run: () => Promise<{ data: Repair }>;
  resultSummary: (r: Repair) => string;
}

export interface MaintenanceTool<Scan, Repair = never> {
  key: string;
  title: string;
  description: string;
  kind: 'repair' | 'report';
  scan: () => Promise<{ data: Scan }>;
  stats: (d: Scan) => StatTile[];
  columns: ColumnDef[];
  items: (d: Scan) => Record<string, unknown>[];
  repair?: RepairConfig<Scan, Repair>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const TONE_STAT: Record<StatTone, string> = {
  ok: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
  warn: 'bg-amber-50 text-amber-700 border border-amber-100',
  mute: 'bg-slate-100 text-slate-500 border border-slate-200',
};

const TONE_VALUE: Record<StatTone, string> = {
  ok: 'text-emerald-700',
  warn: 'text-amber-700',
  mute: 'text-slate-500',
};

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    if (value.length <= 3) return value.join(', ');
    return `${value.length} items`;
  }
  return String(value);
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props<Scan, Repair> {
  tool: MaintenanceTool<Scan, Repair>;
}

type Phase = 'idle' | 'scanning' | 'scanned' | 'repairing' | 'repaired';

export function MaintenanceCard<Scan, Repair>({ tool }: Props<Scan, Repair>) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [scanData, setScanData] = useState<Scan | null>(null);
  const [repairResult, setRepairResult] = useState<Repair | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [repairError, setRepairError] = useState<string | null>(null);

  const runScan = async () => {
    setPhase('scanning');
    setScanError(null);
    setRepairResult(null);
    try {
      const res = await tool.scan();
      setScanData(res.data);
      setPhase('scanned');
    } catch (err: unknown) {
      setScanError(err instanceof Error ? err.message : 'Scan failed');
      setPhase('idle');
    }
  };

  const runRepair = async () => {
    if (!tool.repair || !scanData) return;
    setPhase('repairing');
    setRepairError(null);
    try {
      const res = await tool.repair.run();
      setRepairResult(res.data);
      setPhase('repaired');
      // Re-scan after successful repair
      const scanRes = await tool.scan();
      setScanData(scanRes.data);
      setPhase('scanned');
    } catch (err: unknown) {
      setRepairError(err instanceof Error ? err.message : 'Repair failed');
      setPhase('scanned');
    }
  };

  const isScanning = phase === 'scanning';
  const isRepairing = phase === 'repairing';
  const hasScanned = phase === 'scanned' || phase === 'repaired';

  const rows = scanData && hasScanned ? tool.items(scanData) : [];
  const stats = scanData && hasScanned ? tool.stats(scanData) : [];
  const repairEnabled =
    tool.repair && scanData ? tool.repair.enabled(scanData) : false;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col">
      {/* Card header */}
      <div className="px-5 pt-5 pb-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-slate-900 leading-tight">{tool.title}</h3>
              <span
                className={[
                  'inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full',
                  tool.kind === 'repair'
                    ? 'bg-blue-50 text-blue-700 border border-blue-100'
                    : 'bg-slate-100 text-slate-500 border border-slate-200',
                ].join(' ')}
              >
                {tool.kind === 'repair' ? (
                  <WrenchScrewdriverIcon className="w-3 h-3" />
                ) : (
                  <DocumentTextIcon className="w-3 h-3" />
                )}
                {tool.kind === 'repair' ? 'Repair' : 'Report'}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{tool.description}</p>
          </div>
          <button
            onClick={runScan}
            disabled={isScanning || isRepairing}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`w-3.5 h-3.5 ${isScanning ? 'animate-spin' : ''}`} />
            {hasScanned ? 'Re-scan' : 'Scan'}
          </button>
        </div>

        {/* Scan error */}
        {scanError && (
          <div className="mt-3 flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">
            <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0 mt-px" />
            <span>{scanError}</span>
            <button
              onClick={runScan}
              className="ml-auto text-red-700 underline hover:no-underline whitespace-nowrap"
            >
              Retry
            </button>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 px-5 py-4 space-y-4">
        {/* Idle state */}
        {phase === 'idle' && !scanError && (
          <p className="text-xs text-slate-400 text-center py-4">
            Run a scan to check status.
          </p>
        )}

        {/* Scanning spinner */}
        {isScanning && (
          <div className="flex items-center justify-center py-6 gap-2 text-slate-400 text-xs">
            <ArrowPathIcon className="w-4 h-4 animate-spin" />
            Scanning…
          </div>
        )}

        {/* Stat tiles */}
        {hasScanned && stats.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {stats.map((s) => {
              const tone = s.tone ?? 'mute';
              return (
                <div
                  key={s.label}
                  className={`rounded-lg px-3 py-2.5 text-center ${TONE_STAT[tone]}`}
                >
                  <div className={`text-lg font-bold tabular-nums ${TONE_VALUE[tone]}`}>
                    {s.value}
                  </div>
                  <div className="text-[10px] font-medium uppercase tracking-wide mt-0.5 opacity-80">
                    {s.label}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Items table */}
        {hasScanned && (
          <>
            {rows.length === 0 ? (
              <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2.5">
                <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                Nothing needs attention.
              </div>
            ) : (
              <div className="overflow-x-auto -mx-1">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-100">
                      {tool.columns.map((col) => (
                        <th
                          key={col.key}
                          className="text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400 px-1 pb-1.5"
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr
                        key={i}
                        className="border-b border-slate-50 hover:bg-slate-50 transition-colors"
                      >
                        {tool.columns.map((col) => (
                          <td
                            key={col.key}
                            className="px-1 py-1.5 text-slate-700 max-w-[160px] truncate"
                            title={String(row[col.key] ?? '')}
                          >
                            {formatCell(row[col.key])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Repair result banner */}
        {repairResult && tool.repair && (
          <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-100 rounded-lg text-xs text-emerald-700">
            <CheckCircleIcon className="w-4 h-4 flex-shrink-0 mt-px" />
            <span>{tool.repair.resultSummary(repairResult)}</span>
          </div>
        )}

        {/* Repair error */}
        {repairError && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">
            <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0 mt-px" />
            <span>{repairError}</span>
          </div>
        )}
      </div>

      {/* Footer — repair action */}
      {tool.kind === 'repair' && tool.repair && hasScanned && (
        <div className="px-5 pb-4 pt-2 border-t border-slate-100">
          <button
            onClick={runRepair}
            disabled={!repairEnabled || isRepairing || isScanning}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg transition-colors
              bg-blue-600 hover:bg-blue-700 text-white
              disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
          >
            {isRepairing && <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />}
            {isRepairing ? 'Repairing…' : (scanData ? tool.repair.label(scanData) : 'Repair')}
          </button>
        </div>
      )}
    </div>
  );
}

export default MaintenanceCard;
